// The attachment limits the server is enforcing right now (files per message, pages per PDF, sizes), so the
// attach box can check and hint with the real numbers. An operator can change them without a client release.
//
// Never fails: when the numbers cannot be fetched or make no sense, the defaults are used, which are what the
// server enforces unless it has been reconfigured. The server checks every upload and every turn itself, so a
// stale or default number here only changes what is refused a step earlier or later.
import { useEffect, useState } from 'react';
import { AttachmentLimits, DEFAULT_ATTACHMENT_LIMITS } from './classifyAttachments';

const positiveInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback;

export const fetchAttachmentLimits = async (apiUrl: string): Promise<AttachmentLimits> => {
  try {
    const response = await fetch(`${apiUrl}/classify/hts/attachment-limits`);
    if (!response.ok) {
      return DEFAULT_ATTACHMENT_LIMITS;
    }
    const body = await response.json();
    return {
      maxFilesPerTurn: positiveInteger(body?.max_files_per_turn, DEFAULT_ATTACHMENT_LIMITS.maxFilesPerTurn),
      maxPdfPages: positiveInteger(body?.max_pdf_pages, DEFAULT_ATTACHMENT_LIMITS.maxPdfPages),
      maxPdfBytes: positiveInteger(body?.max_pdf_bytes, DEFAULT_ATTACHMENT_LIMITS.maxPdfBytes),
      maxImageBytes: positiveInteger(body?.max_image_bytes, DEFAULT_ATTACHMENT_LIMITS.maxImageBytes),
      maxMessageBytes: positiveInteger(
        body?.max_message_attachment_bytes,
        DEFAULT_ATTACHMENT_LIMITS.maxMessageBytes,
      ),
    };
  } catch {
    return DEFAULT_ATTACHMENT_LIMITS;
  }
};

/** The limits in force: the defaults at first, then the server's once they arrive. */
export const useAttachmentLimits = (apiUrl: string): AttachmentLimits => {
  const [limits, setLimits] = useState<AttachmentLimits>(DEFAULT_ATTACHMENT_LIMITS);
  useEffect(() => {
    let cancelled = false;
    fetchAttachmentLimits(apiUrl).then((fetched) => {
      if (!cancelled) {
        setLimits(fetched);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);
  return limits;
};
